<script >
import { EMPTY_METRIC_VALUE, resolveChartingMetricValue } from '@/features/charting-next/models/chartingMetricPreferences';
import { formatNumberLabel } from '@/features/charting-next/adapters/chartingAdapterUtils';
import { computed, onBeforeUnmount, onMounted, ref } from '@vue/composition-api';
const __sfc_main = {};
__sfc_main.props = {
  results: {
    type: Array,
    default: () => []
  },
  selectedTicker: {
    type: String,
    default: ''
  },
  columns: {
    type: Array,
    default: () => []
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  emptyMessage: {
    type: String,
    default: ''
  },
  sortValue: {
    type: String,
    default: 'original'
  },
  sortOptions: {
    type: Array,
    default: () => []
  },
  totalResults: {
    type: Number,
    default: 0
  },
  hasMoreResults: {
    type: Boolean,
    default: false
  },
  isLoadingNextPage: {
    type: Boolean,
    default: false
  },
  pageError: {
    type: [Object, String],
    default: null
  },
  documentScrollPagination: {
    type: Boolean,
    default: false
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const emit = __ctx.emit;
  const pageSentinelRef = ref(null);
  let pageObserver;
  const parsedSort = computed(() => {
    const [field = '', direction = ''] = String(props.sortValue || '').split(':');
    return {
      field,
      direction
    };
  });
  const sortableFields = computed(() => new Set(props.sortOptions.map(option => String(option.value || '').split(':')[0]).filter(field => field && field !== 'original')));
  const formatDisplayValue = value => {
    const text = String(value ?? '').trim();
    const numericMatch = text.match(/^(-?\d+(?:\.\d+)?)([%€$])?$/);
    if (!numericMatch) {
      return value;
    }
    const [, numericValue, suffix = ''] = numericMatch;
    const normalizedNumber = formatNumberLabel(numericValue);
    return `${normalizedNumber}${suffix}`;
  };
  const formattedResultCount = computed(() => formatNumberLabel(props.results.length, {
    maximumFractionDigits: 0
  }));
  const formattedTotalResults = computed(() => formatNumberLabel(props.totalResults || props.results.length, {
    maximumFractionDigits: 0
  }));
  const tableRows = computed(() => props.results.map(stock => ({
    id: stock.id || stock.ticker,
    stock,
    cells: props.columns.map(column => {
      const value = resolveChartingMetricValue(column, stock);
      return {
        field: column.field,
        value: formatDisplayValue(value),
        isEmpty: value === EMPTY_METRIC_VALUE
      };
    })
  })));
  const isSortableField = field => sortableFields.value.has(field);
  const isSortedField = field => parsedSort.value.field === field;
  const sortIcon = field => {
    if (!isSortedField(field)) {
      return 'pi pi-sort-alt';
    }
    return parsedSort.value.direction === 'desc' ? 'pi pi-sort-amount-down' : 'pi pi-sort-amount-up';
  };
  const changeSort = field => {
    if (!isSortableField(field)) {
      return;
    }
    const nextDirection = isSortedField(field) && parsedSort.value.direction === 'asc' ? 'desc' : 'asc';
    emit('change-sort', `${field}:${nextDirection}`);
  };
  const selectTicker = ticker => {
    if (ticker) {
      emit('select-ticker', ticker);
    }
  };
  const selectEntryVariant = (ticker, entryVariantKey) => {
    if (ticker && entryVariantKey) {
      emit('select-entry-variant', {
        ticker,
        entryVariantKey
      });
    }
  };
  const handleScroll = event => {
    if (props.documentScrollPagination || !props.hasMoreResults || props.isLoadingNextPage || !event?.target) return;
    const element = event.target;
    if (element.scrollHeight - element.scrollTop - element.clientHeight <= 240) emit('load-more');
  };
  onMounted(() => {
    if (!props.documentScrollPagination || typeof IntersectionObserver === 'undefined' || !pageSentinelRef.value) return;
    pageObserver = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting) && props.hasMoreResults && !props.isLoadingNextPage) emit('load-more');
    }, {
      root: null,
      rootMargin: '700px 0px',
      threshold: 0
    });
    pageObserver.observe(pageSentinelRef.value);
  });
  onBeforeUnmount(() => pageObserver?.disconnect());
  return {
    emit,
    pageSentinelRef,
    formattedResultCount,
    formattedTotalResults,
    tableRows,
    isSortableField,
    isSortedField,
    sortIcon,
    changeSort,
    selectTicker,
    selectEntryVariant,
    handleScroll
  };
};
export default __sfc_main;
</script>

<template>
    <section class="charting-b-table-stage">
        <div class="charting-b-table-stage__header">
            <div>
                <h2 class="charting-b-table-stage__title">Tabla de valores</h2>
                <p class="charting-b-table-stage__count">{{ isLoading ? 'Actualizando...' : `${formattedResultCount} de ${formattedTotalResults} valores` }}</p>
            </div>
        </div>

        <div v-if="isLoading" class="charting-b-table-stage__status">
            <ProgressSpinner style="width: 32px; height: 32px" stroke-width="6" />
        </div>
        <div v-else-if="!tableRows.length" class="charting-b-table-stage__status">
            <div class="brand-empty-state rounded-xl border border-dashed p-4 text-sm">
                {{ emptyMessage || 'No hay valores visibles para la fuente actual.' }}
            </div>
        </div>
        <div v-else class="charting-b-table-stage__frame">
            <div class="charting-b-table-stage__scroll" @scroll.passive="handleScroll">
                <table class="charting-b-table-stage__table">
                    <thead>
                        <tr>
                            <th class="charting-b-table-stage__ticker-column">
                                <button type="button" class="charting-b-table-stage__header-button" @click="changeSort('ticker')">
                                    <span>Valor</span>
                                    <i :class="sortIcon('ticker')" aria-hidden="true"></i>
                                </button>
                            </th>
                            <th v-for="column in columns" :key="column.field">
                                <button v-if="isSortableField(column.field)" type="button" class="charting-b-table-stage__header-button" :class="{ 'is-active': isSortedField(column.field) }" @click="changeSort(column.field)">
                                    <span>{{ column.label }}</span>
                                    <i :class="sortIcon(column.field)" aria-hidden="true"></i>
                                </button>
                                <span v-else class="charting-b-table-stage__header-label">{{ column.label }}</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="row in tableRows"
                            :key="row.id"
                            :class="{ 'is-selected': row.stock.ticker === selectedTicker }"
                            tabindex="0"
                            @click="selectTicker(row.stock.ticker)"
                            @keydown.enter.prevent="selectTicker(row.stock.ticker)"
                            @keydown.space.prevent="selectTicker(row.stock.ticker)"
                        >
                            <th class="charting-b-table-stage__ticker-column" scope="row">
                                <div class="charting-b-table-stage__identity">
                                    <span class="charting-b-table-stage__ticker">{{ row.stock.ticker }}</span>
                                    <span v-if="(row.stock.entryVariants && row.stock.entryVariants.length) || row.stock.entryTypeLabel" class="charting-b-table-stage__separator">·</span>
                                    <span v-if="row.stock.entryVariants && row.stock.entryVariants.length" class="charting-b-table-stage__entries">
                                        <button
                                            v-for="variant in row.stock.entryVariants"
                                            :key="variant.entryVariantKey"
                                            type="button"
                                            class="charting-b-table-stage__entry-pill"
                                            :class="{ 'is-active': variant.entryVariantKey === row.stock.activeEntryVariantKey }"
                                            @click.stop="selectEntryVariant(row.stock.ticker, variant.entryVariantKey)"
                                        >
                                            {{ variant.entrySwitchLabel }}
                                        </button>
                                    </span>
                                    <span v-else-if="row.stock.entryTypeLabel" class="charting-b-table-stage__entry-pill is-active">{{ row.stock.entryTypeLabel }}</span>
                                </div>
                                <div class="charting-b-table-stage__company">{{ row.stock.companyName || row.stock.description || 'N/D' }}</div>
                            </th>
                            <td v-for="cell in row.cells" :key="cell.field" :class="{ 'is-empty': cell.isEmpty }">
                                {{ cell.value }}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div v-if="isLoadingNextPage" class="charting-b-table-stage__page-state">Cargando más valores…</div>
                <button v-else-if="pageError" type="button" class="charting-b-table-stage__page-retry" @click="emit('retry')">Reintentar carga</button>
            </div>
        </div>
        <div v-if="documentScrollPagination" ref="pageSentinelRef" class="charting-b-table-stage__document-sentinel" aria-hidden="true"></div>
    </section>
</template>

<style scoped>
.charting-b-table-stage {
    display: flex;
    min-width: 0;
    min-height: 0;
    height: 100%;
    flex-direction: column;
    overflow: hidden;
}

.charting-b-table-stage__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding-bottom: 0.65rem;
}

.charting-b-table-stage__title {
    margin: 0;
    color: var(--p-text-color);
    font-size: 1.05rem;
    font-weight: 800;
    line-height: 1.15;
}

.charting-b-table-stage__count {
    margin: 0.18rem 0 0;
    color: var(--p-text-muted-color);
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1.1;
}

.charting-b-table-stage__status {
    display: flex;
    min-height: 0;
    flex: 1 1 auto;
    align-items: center;
    justify-content: center;
}

.charting-b-table-stage__frame {
    min-height: 0;
    flex: 1 1 auto;
    overflow: hidden;
    border: 1px solid var(--charting-b-border-color);
    border-radius: 1rem;
    background: var(--charting-b-panel-bg-strong);
}

.charting-b-table-stage__scroll {
    width: 100%;
    height: 100%;
    overflow: auto;
}

.charting-b-table-stage__page-state,
.charting-b-table-stage__page-retry {
    position: sticky;
    left: 0;
    display: block;
    width: 100%;
    padding: 0.7rem;
    text-align: center;
    color: var(--p-text-muted-color);
    font-size: 0.76rem;
}

.charting-b-table-stage__document-sentinel {
    width: 100%;
    height: 1px;
}

.charting-b-table-stage__page-retry {
    border: 0;
    background: var(--charting-b-panel-bg-strong);
    color: var(--brand-primary);
    cursor: pointer;
    text-decoration: underline;
}

.charting-b-table-stage__table {
    width: 100%;
    min-width: 64rem;
    border-collapse: separate;
    border-spacing: 0;
    color: var(--p-text-color);
    font-size: 0.82rem;
}

.charting-b-table-stage__table th,
.charting-b-table-stage__table td {
    border-bottom: 1px solid var(--charting-b-border-color);
    padding: 0.58rem 0.7rem;
    text-align: left;
    vertical-align: top;
    white-space: nowrap;
}

.charting-b-table-stage__table thead th {
    position: sticky;
    z-index: 2;
    top: 0;
    background: color-mix(in srgb, var(--charting-b-panel-bg-strong) 96%, var(--charting-b-panel-bg));
    color: var(--p-text-muted-color);
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.charting-b-table-stage__table tbody tr {
    cursor: pointer;
    transition:
        background-color 0.16s ease,
        color 0.16s ease;
}

.charting-b-table-stage__table tbody tr:hover,
.charting-b-table-stage__table tbody tr.is-selected {
    background-color: var(--charting-b-hover-bg);
}

.charting-b-table-stage__table tbody tr:focus-visible {
    outline: 2px solid color-mix(in srgb, var(--brand-primary), transparent 42%);
    outline-offset: -2px;
}

.charting-b-table-stage__table tbody tr:last-child th,
.charting-b-table-stage__table tbody tr:last-child td {
    border-bottom: 0;
}

.charting-b-table-stage__ticker-column {
    position: sticky;
    z-index: 2;
    left: 0;
    width: 16rem;
    min-width: 16rem;
    max-width: 16rem;
    overflow: hidden;
    background-color: var(--charting-b-panel-bg-strong);
    box-shadow: 1px 0 0 var(--charting-b-border-color);
    background-clip: padding-box;
    transition:
        background-color 0.16s ease,
        color 0.16s ease;
}

.charting-b-table-stage__table thead .charting-b-table-stage__ticker-column {
    z-index: 30;
    isolation: isolate;
    background-color: color-mix(in srgb, var(--charting-b-panel-bg-strong) 96%, var(--charting-b-panel-bg));
    box-shadow: 1px 0 0 var(--charting-b-border-color);
}

.charting-b-table-stage__table thead .charting-b-table-stage__ticker-column::before {
    content: '';
    position: absolute;
    z-index: -1;
    inset: 0;
    background: color-mix(in srgb, var(--charting-b-panel-bg-strong) 96%, var(--charting-b-panel-bg));
}

.charting-b-table-stage__table thead .charting-b-table-stage__ticker-column::after {
    content: '';
    position: absolute;
    z-index: 1;
    top: 0;
    right: -1px;
    bottom: 0;
    width: 1px;
    background: var(--charting-b-border-color);
}

.charting-b-table-stage__table tbody tr:hover .charting-b-table-stage__ticker-column,
.charting-b-table-stage__table tbody tr.is-selected .charting-b-table-stage__ticker-column {
    background-color: var(--charting-b-hover-bg);
}

.charting-b-table-stage__header-button {
    display: inline-flex;
    min-width: 0;
    align-items: center;
    gap: 0.35rem;
    border: 0;
    background: transparent;
    padding: 0;
    color: inherit;
    font: inherit;
    letter-spacing: inherit;
    text-transform: inherit;
    cursor: pointer;
}

.charting-b-table-stage__header-button.is-active {
    color: var(--p-text-color);
}

.charting-b-table-stage__header-label {
    display: inline-flex;
}

.charting-b-table-stage__identity {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 0.34rem;
}

.charting-b-table-stage__ticker {
    flex: 0 0 auto;
    color: var(--p-text-color);
    font-size: 0.86rem;
    font-weight: 850;
    line-height: 1.12;
}

.charting-b-table-stage__separator {
    flex: 0 0 auto;
    color: var(--p-text-muted-color);
    font-size: 0.72rem;
    font-weight: 850;
}

.charting-b-table-stage__entries {
    display: inline-flex;
    min-width: 0;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.22rem;
}

.charting-b-table-stage__company {
    overflow: hidden;
    margin-top: 0.18rem;
    color: var(--p-text-muted-color);
    font-size: 0.72rem;
    line-height: 1.15;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.charting-b-table-stage__entry-pill {
    display: inline-flex;
    max-width: 9rem;
    align-items: center;
    overflow: hidden;
    border: 1px solid var(--charting-b-border-color);
    border-radius: 999px;
    background: var(--charting-b-panel-bg);
    padding: 0.12rem 0.38rem;
    color: var(--p-text-muted-color);
    font-size: 0.66rem;
    font-weight: 800;
    line-height: 1;
    text-overflow: ellipsis;
    white-space: nowrap;
}

button.charting-b-table-stage__entry-pill {
    cursor: pointer;
}

.charting-b-table-stage__entry-pill.is-active {
    border-color: color-mix(in srgb, var(--brand-primary), transparent 46%);
    background: color-mix(in srgb, var(--brand-primary), transparent 84%);
    color: var(--brand-primary);
}

.charting-b-table-stage__table td.is-empty {
    color: color-mix(in srgb, var(--p-text-muted-color) 72%, transparent);
}
</style>
